import { cn, cva } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementType } from 'react';

// Lowercase fill values
export type BadgeFill = 'full' | 'medium' | 'light' | 'none';

const badgeVariants = cva('inline-flex items-center font-medium', {
    variants: {
        variant: {
            flat: '',
            bordered: 'border outline-none',
        },
        size: {
            sm: 'h-4 px-1.5 text-xs',
            md: 'h-5 px-2 text-sm',
            lg: 'h-6 px-2.5 text-md',
        },
        fill: {
            full: '',
            medium: '',
            light: '',
            none: 'bg-transparent',
        },
        color: {
            gray: '',
            purple: '',
            blue: '',
            yellow: '',
            green: '',
            red: '',
            cyan: '',
        },
        shape: {
            rounded: 'rounded-md',
            pill: 'rounded-full',
        },
    },
    defaultVariants: {
        variant: 'flat',
        size: 'md',
        fill: 'full',
        color: 'gray',
        shape: 'rounded',
    },
    compoundVariants: [
        // Full fill badges - border colors match background for all variants
        {
            fill: ['full'],
            color: 'gray',
            className: 'border-gray-500 bg-gray-500 text-black',
        },
        {
            fill: ['full'],
            color: 'purple',
            className: 'border-purple-500 bg-purple-500 text-black',
        },
        {
            fill: ['full'],
            color: 'blue',
            className: 'border-blue-500 bg-blue-500 text-black',
        },
        {
            fill: ['full'],
            color: 'yellow',
            className: 'border-yellow-500 bg-yellow-500 text-black',
        },
        {
            fill: ['full'],
            color: 'green',
            className: 'border-green-500 bg-green-500 text-black',
        },
        {
            fill: ['full'],
            color: 'red',
            className: 'border-red-500 bg-red-500 text-black',
        },
        {
            fill: ['full'],
            color: 'cyan',
            className: 'border-cyan-500 bg-cyan-500 text-black',
        },

        // Medium fill badges - 40% background opacity
        // Flat variant - base styles only
        {
            fill: ['medium'],
            variant: 'flat',
            color: 'gray',
            className: 'bg-gray-500/40 text-gray-500',
        },
        {
            fill: ['medium'],
            variant: 'flat',
            color: 'purple',
            className: 'bg-purple-500/40 text-purple-500',
        },
        {
            fill: ['medium'],
            variant: 'flat',
            color: 'blue',
            className: 'bg-blue-500/40 text-blue-500',
        },
        {
            fill: ['medium'],
            variant: 'flat',
            color: 'yellow',
            className: 'bg-yellow-500/40 text-yellow-500',
        },
        {
            fill: ['medium'],
            variant: 'flat',
            color: 'green',
            className: 'bg-green-500/40 text-green-500',
        },
        {
            fill: ['medium'],
            variant: 'flat',
            color: 'red',
            className: 'bg-red-500/40 text-red-500',
        },
        {
            fill: ['medium'],
            variant: 'flat',
            color: 'cyan',
            className: 'bg-cyan-500/40 text-cyan-500',
        },

        // Medium fill - Bordered variant - 80% border opacity, brighter border + glow on focus
        {
            fill: ['medium'],
            variant: 'bordered',
            color: 'gray',
            className:
                'border-gray-500/80 bg-gray-500/40 text-gray-500 focus-within:border-gray-500 focus-within:ring-1 focus-within:ring-gray-500/40 focus:border-gray-500 focus:ring-1 focus:ring-gray-500/40',
        },
        {
            fill: ['medium'],
            variant: 'bordered',
            color: 'purple',
            className:
                'border-purple-500/80 bg-purple-500/40 text-purple-500 focus-within:border-purple-500 focus-within:ring-1 focus-within:ring-purple-500/40 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/40',
        },
        {
            fill: ['medium'],
            variant: 'bordered',
            color: 'blue',
            className:
                'border-blue-500/80 bg-blue-500/40 text-blue-500 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/40 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40',
        },
        {
            fill: ['medium'],
            variant: 'bordered',
            color: 'yellow',
            className:
                'border-yellow-500/80 bg-yellow-500/40 text-yellow-500 focus-within:border-yellow-500 focus-within:ring-1 focus-within:ring-yellow-500/40 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/40',
        },
        {
            fill: ['medium'],
            variant: 'bordered',
            color: 'green',
            className:
                'border-green-500/80 bg-green-500/40 text-green-500 focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500/40 focus:border-green-500 focus:ring-1 focus:ring-green-500/40',
        },
        {
            fill: ['medium'],
            variant: 'bordered',
            color: 'red',
            className:
                'border-red-500/80 bg-red-500/40 text-red-500 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500/40 focus:border-red-500 focus:ring-1 focus:ring-red-500/40',
        },
        {
            fill: ['medium'],
            variant: 'bordered',
            color: 'cyan',
            className:
                'border-cyan-500/80 bg-cyan-500/40 text-cyan-500 focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500/40 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/40',
        },

        // Light fill badges - 20% background opacity
        // Flat variant - base styles only
        {
            fill: ['light'],
            variant: 'flat',
            color: 'gray',
            className: 'bg-gray-500/20 text-gray-500',
        },
        {
            fill: ['light'],
            variant: 'flat',
            color: 'purple',
            className: 'bg-purple-500/20 text-purple-500',
        },
        {
            fill: ['light'],
            variant: 'flat',
            color: 'blue',
            className: 'bg-blue-500/20 text-blue-500',
        },
        {
            fill: ['light'],
            variant: 'flat',
            color: 'yellow',
            className: 'bg-yellow-500/20 text-yellow-500',
        },
        {
            fill: ['light'],
            variant: 'flat',
            color: 'green',
            className: 'bg-green-500/20 text-green-500',
        },
        {
            fill: ['light'],
            variant: 'flat',
            color: 'red',
            className: 'bg-red-500/20 text-red-500',
        },
        {
            fill: ['light'],
            variant: 'flat',
            color: 'cyan',
            className: 'bg-cyan-500/20 text-cyan-500',
        },

        // Light fill - Bordered variant - 40% border opacity, brighter border + glow on focus
        {
            fill: ['light'],
            variant: 'bordered',
            color: 'gray',
            className:
                'border-gray-500/40 bg-gray-500/20 text-gray-500 focus-within:border-gray-500 focus-within:ring-1 focus-within:ring-gray-500/40 focus:border-gray-500 focus:ring-1 focus:ring-gray-500/40',
        },
        {
            fill: ['light'],
            variant: 'bordered',
            color: 'purple',
            className:
                'border-purple-500/40 bg-purple-500/20 text-purple-500 focus-within:border-purple-500 focus-within:ring-1 focus-within:ring-purple-500/40 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/40',
        },
        {
            fill: ['light'],
            variant: 'bordered',
            color: 'blue',
            className:
                'border-blue-500/40 bg-blue-500/20 text-blue-500 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/40 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40',
        },
        {
            fill: ['light'],
            variant: 'bordered',
            color: 'yellow',
            className:
                'border-yellow-500/40 bg-yellow-500/20 text-yellow-500 focus-within:border-yellow-500 focus-within:ring-1 focus-within:ring-yellow-500/40 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/40',
        },
        {
            fill: ['light'],
            variant: 'bordered',
            color: 'green',
            className:
                'border-green-500/40 bg-green-500/20 text-green-500 focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500/40 focus:border-green-500 focus:ring-1 focus:ring-green-500/40',
        },
        {
            fill: ['light'],
            variant: 'bordered',
            color: 'red',
            className:
                'border-red-500/40 bg-red-500/20 text-red-500 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500/40 focus:border-red-500 focus:ring-1 focus:ring-red-500/40',
        },
        {
            fill: ['light'],
            variant: 'bordered',
            color: 'cyan',
            className:
                'border-cyan-500/40 bg-cyan-500/20 text-cyan-500 focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500/40 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/40',
        },

        // None fill badges - transparent background
        // Flat variant - base styles only
        {
            fill: ['none'],
            variant: 'flat',
            color: 'gray',
            className: 'text-gray-500',
        },
        {
            fill: ['none'],
            variant: 'flat',
            color: 'purple',
            className: 'text-purple-500',
        },
        {
            fill: ['none'],
            variant: 'flat',
            color: 'blue',
            className: 'text-blue-500',
        },
        {
            fill: ['none'],
            variant: 'flat',
            color: 'yellow',
            className: 'text-yellow-500',
        },
        {
            fill: ['none'],
            variant: 'flat',
            color: 'green',
            className: 'text-green-500',
        },
        {
            fill: ['none'],
            variant: 'flat',
            color: 'red',
            className: 'text-red-500',
        },
        {
            fill: ['none'],
            variant: 'flat',
            color: 'cyan',
            className: 'text-cyan-500',
        },

        // None fill - Bordered variant - 40% border opacity, brighter border + glow on focus
        {
            fill: ['none'],
            variant: 'bordered',
            color: 'gray',
            className:
                'border-gray-500/40 text-gray-500 focus-within:border-gray-500 focus-within:ring-1 focus-within:ring-gray-500/40 focus:border-gray-500 focus:ring-1 focus:ring-gray-500/40',
        },
        {
            fill: ['none'],
            variant: 'bordered',
            color: 'purple',
            className:
                'border-purple-500/40 text-purple-500 focus-within:border-purple-500 focus-within:ring-1 focus-within:ring-purple-500/40 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/40',
        },
        {
            fill: ['none'],
            variant: 'bordered',
            color: 'blue',
            className:
                'border-blue-500/40 text-blue-500 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/40 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40',
        },
        {
            fill: ['none'],
            variant: 'bordered',
            color: 'yellow',
            className:
                'border-yellow-500/40 text-yellow-500 focus-within:border-yellow-500 focus-within:ring-1 focus-within:ring-yellow-500/40 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/40',
        },
        {
            fill: ['none'],
            variant: 'bordered',
            color: 'green',
            className:
                'border-green-500/40 text-green-500 focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500/40 focus:border-green-500 focus:ring-1 focus:ring-green-500/40',
        },
        {
            fill: ['none'],
            variant: 'bordered',
            color: 'red',
            className:
                'border-red-500/40 text-red-500 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500/40 focus:border-red-500 focus:ring-1 focus:ring-red-500/40',
        },
        {
            fill: ['none'],
            variant: 'bordered',
            color: 'cyan',
            className:
                'border-cyan-500/40 text-cyan-500 focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500/40 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/40',
        },
    ],
});

export type BadgeColor = 'gray' | 'purple' | 'blue' | 'yellow' | 'green' | 'red' | 'cyan';
export type BadgeShape = 'rounded' | 'pill';

type BadgeOwnProps = {
    fill?: BadgeFill;
    color?: BadgeColor;
    shape?: BadgeShape;
} & Omit<VariantProps<typeof badgeVariants>, 'fill'>;

type BadgeProps<T extends ElementType = 'span'> = BadgeOwnProps & {
    as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof BadgeOwnProps | 'as'>;

const Badge = forwardRef<HTMLElement, BadgeProps<ElementType>>(
    ({ className, variant, size, fill, color, shape, children, as, ...props }, ref) => {
        const Component = (as || 'span') as ElementType;

        return (
            <Component
                ref={ref}
                className={cn(badgeVariants({ variant, size, fill, color, shape, className }))}
                {...props}
            >
                {children}
            </Component>
        );
    },
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
export type { BadgeProps };
